/**
 * ISO 27001 Score Calculation
 * ISO27001 = 40% Self-Assessment + 60% Annex A
 * Annex A: IMPLEMENTED=100, PARTIAL=50, NOT_IMPLEMENTED=0, N_A=excluded
 */

export type AnnexAControlStatus = 'IMPLEMENTED' | 'PARTIAL' | 'NOT_IMPLEMENTED' | 'N_A';

const STATUS_TO_PERCENT: Record<AnnexAControlStatus, number | null> = {
  IMPLEMENTED: 100,
  PARTIAL: 50,
  NOT_IMPLEMENTED: 0,
  N_A: null, // excluded from denominator
};

function normalizeStatus(s: string | undefined | null): AnnexAControlStatus | null {
  if (!s || typeof s !== 'string') return null;
  const u = s.toUpperCase().replace(/\s+/g, '_');
  if (u === 'IMPLEMENTED' || u === 'PARTIAL' || u === 'NOT_IMPLEMENTED' || u === 'N_A' || u === 'NA') {
    return (u === 'NA' ? 'N_A' : u) as AnnexAControlStatus;
  }
  return null;
}

/**
 * Compute Annex A score from control answers.
 * answers: Record<controlCode, status>
 */
export function computeAnnexAScore(controlsAnswers: Record<string, string>): {
  score: number;
  breakdown: { implemented: number; partial: number; notImplemented: number; na: number; totalNonNA: number };
} {
  const breakdown = { implemented: 0, partial: 0, notImplemented: 0, na: 0, totalNonNA: 0 };
  let sum = 0;

  for (const raw of Object.values(controlsAnswers)) {
    const status = normalizeStatus(raw);
    if (!status) continue;

    if (status === 'N_A') {
      breakdown.na++;
      continue;
    }

    breakdown.totalNonNA++;
    const pct = STATUS_TO_PERCENT[status];
    if (pct !== null) sum += pct;

    if (status === 'IMPLEMENTED') breakdown.implemented++;
    else if (status === 'PARTIAL') breakdown.partial++;
    else breakdown.notImplemented++;
  }

  const score = breakdown.totalNonNA > 0
    ? Math.round((sum / (breakdown.totalNonNA * 100)) * 100)
    : 0;

  return { score, breakdown };
}

/**
 * Compute ISO 27001 total score: 40% Self-Assessment + 60% Annex A
 */
export function computeIso27001Score(params: {
  selfAssessmentScore: number | null | undefined;
  annexAScore: number | null | undefined;
}): number {
  const sa = typeof params.selfAssessmentScore === 'number' && Number.isFinite(params.selfAssessmentScore)
    ? Math.max(0, Math.min(100, params.selfAssessmentScore))
    : 0;
  const aa = typeof params.annexAScore === 'number' && Number.isFinite(params.annexAScore)
    ? Math.max(0, Math.min(100, params.annexAScore))
    : 0;

  if (sa === 0 && aa === 0) return 0;
  return Math.round(0.4 * sa + 0.6 * aa);
}
